/**
 * O ESTOQUE DE RAÇÃO — o galpão.
 *
 * ── O TRATO BAIXA O ESTOQUE ───────────────────────────────────────────────
 *
 * Decisão do gestor em 22/09/2026, revendo a dele mesma. A anterior era que a
 * baixa fosse lançamento próprio, e a diferença entre o galpão e o trato
 * medisse o desperdício. Durou um dia, e a derrubada foi correta.
 *
 * O que a derrubou não é o trabalho a mais: é o que o segundo registro mede.
 * Baixa digitada de memória no fim do dia, arredondada para saco inteiro, mede
 * o cuidado de quem digitou — não o desperdício do galpão. Segundo registro
 * ruim é PIOR que nenhum, porque produz alarme falso, e alarme falso ensina
 * todo mundo a ignorar o número.
 *
 * No granel fica descarado: a balança já deu o quilo exato no trato, e a baixa
 * seria redigitar o mesmo número.
 *
 * E há o ganho que não estava na conta. Com a saída manual, se ninguém lançar,
 * o saldo mente para cima indefinidamente e nada avisa. Com o trato baixando,
 * o saldo só erra se o trato errar — e o trato é a única coisa que a operação
 * nunca esquece, porque é o serviço.
 *
 * ── ENTÃO ESTA TELA NÃO É DE LANÇAMENTO DIÁRIO ────────────────────────────
 *
 * Ela é de LEITURA, e os dois lançamentos que sobraram não são de todo dia: a
 * COMPRA, que acontece quando o caminhão chega, e a PERDA, que acontece quando
 * alguma coisa dá errado. O consumo entra sozinho, pela Ronda e pelo Trato.
 *
 * ── O QUE FALTA, DE PROPÓSITO ─────────────────────────────────────────────
 *
 * O PRIMEIRO A ENTRAR, PRIMEIRO A SAIR ainda não está aqui. É ele que devolve
 * a auditoria que o confronto perdeu, e de graça: cada compra é uma pilha, o
 * consumo come a mais velha primeiro, e o app passa a dizer quanto resta de
 * CADA pilha. Aí o peixeiro confere olhando, sem medir nada, e só fala quando
 * discorda. É a inversão que vale para o app inteiro — o app fala, o humano
 * corrige.
 *
 * A CONTAGEM PERIÓDICA fica recusada por decisão do gestor: «francamente até
 * gosto da ideia mas não quero implementar». E o AJUSTE — o quarto tipo de
 * `mov_racao`, o único que mexe no saldo sem nada ter entrado nem saído —
 * continua fora: solto, viraria o caminho fácil para fazer o número bater, e
 * ele bateria sempre, sem nunca mais querer dizer nada.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  anularFechamento, anularMovimentoDeRacao, fecharPilha, lancarNotaDeRacao, listarCiclos,
  precosNaFila,
  listarFornecedores, ocultarRacao, parametrosDoCalculo,
  registrarMovimentoDeRacao, verGalpao,
  type Calibragem, type Ciclo, type CompraSemPreco, type Fornecedor, type Galpao,
  type MovimentoDeRacao, type NotaDeRacao, type ParametroDoCalculo, type Perfil, type Pilha,
  type RacaoNoGalpao as RacaoDoGalpao, type TipoDeMovimento,
} from '../acesso/api'
import { porQueNaoRateia, ratearNota, type SemRateio, type UnidadeDaNota } from '../dominio/nota'
import { emReais } from '../dominio/romaneio'
import {
  diasDeRacao, emSacosDe, nuncaComprada, porQueNaoDaParaEstimar,
} from '../dominio/estoque'
import { PILHAS_PARA_CONFIAR } from '../dominio/pilhas'
import { inteiro, numero, numeroNaFaixa, numeroPtBr, soNumero } from '../dominio/unidades'
import { EscolherRacao, nomeDaRacao, type Especificacao } from './EscolherRacao'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'

const RASCUNHO = 'cunhatai.estoque'

interface Rascunho {
  dia: string
  tipo: TipoDeMovimento
  racao: Especificacao | null
  quantidade: string
  unidade: 'saco' | 'kg'
  pesoSaco: string
  fornecedor: string
  marca: string
  documento: string
  /**
   * De antes de 25/09/2026, quando o valor entrava junto com a compra. Lido e
   * ignorado: o preço agora é da nota, lançada à parte.
   */
  custo?: string
  cicloId: string
  observacao: string
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    return bruto ? (JSON.parse(bruto) as Rascunho) : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

/** A especificação de uma linha do galpão, no formato que o seletor entende. */
function espec(r: RacaoDoGalpao): Especificacao | null {
  if (r.granulometria_min_mm === null || r.granulometria_max_mm === null) return null
  if (r.proteina_pct === null) return null
  return { min: r.granulometria_min_mm, max: r.granulometria_max_mm, proteina: r.proteina_pct }
}

export function Estoque({ perfil, aoGravar }: { perfil: Perfil; aoGravar: () => void }) {
  const salvo = useMemo(lerRascunho, [])

  const [galpao, setGalpao] = useState<Galpao | null>(null)
  const [parametros, setParametros] = useState<ParametroDoCalculo[]>([])
  const [ciclos, setCiclos] = useState<Ciclo[]>([])
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  // Verdadeiro quando o nome está sendo digitado por não existir na lista.
  const [fornecedorNovo, setFornecedorNovo] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  // O PADRÃO É A COMPRA. Era a saída, quando ela existia e era diária; das
  // duas que sobraram, a compra é a que traz alguém a esta tela.
  const [tipo, setTipo] = useState<TipoDeMovimento>(salvo?.tipo ?? 'entrada')
  const [racao, setRacao] = useState<Especificacao | null>(salvo?.racao ?? null)
  const [quantidade, setQuantidade] = useState(salvo?.quantidade ?? '')
  const [unidade, setUnidade] = useState<'saco' | 'kg'>(salvo?.unidade ?? 'saco')
  const [pesoSaco, setPesoSaco] = useState(salvo?.pesoSaco ?? '')
  const [fornecedor, setFornecedor] = useState(salvo?.fornecedor ?? '')

  const [marca, setMarca] = useState(salvo?.marca ?? '')
  const [documento, setDocumento] = useState(salvo?.documento ?? '')
  const [cicloId, setCicloId] = useState(salvo?.cicloId ?? '')
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')

  const podeVerCusto = perfil !== 'tratador'
  // O FORMULÁRIO DA NOTA, quando aberto: para lançar (a compra tocada já vem
  // marcada) ou para corrigir uma nota que já vale.
  const [nota, setNota] = useState<{ marcada: string | null; corrigindo: NotaDeRacao | null } | null>(null)
  const ancoraDaNota = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (nota !== null) ancoraDaNota.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [nota])

  useEffect(() => {
    Promise.all([verGalpao(), parametrosDoCalculo(), listarCiclos(), listarFornecedores()])
      .then(([g, p, c, f]) => {
        setGalpao(g)
        setParametros(p.parametros)
        setCiclos(c.filter((x) => !x.encerrado))
        setFornecedores(f.filter((x) => x.ativo))
        // O PESO DO SACO JÁ VEM PREENCHIDO com o da fazenda — pedido do
        // gestor. Campo vazio com um `25` cinza de sugestão faz o dedo
        // apressado pular, e aí a compra inteira converte por um peso que
        // ninguém confirmou. Preenchido, ele é uma afirmação que se troca.
        setPesoSaco((atual) => (atual === '' ? emTexto(g.peso_saco_kg) : atual))
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [])

  useEffect(() => {
    const temAlgo = quantidade !== '' || racao !== null
    guardarRascunho(
      temAlgo
        ? { dia, tipo, racao, quantidade, unidade, pesoSaco, fornecedor, marca,
            documento, cicloId, observacao }
        : null,
    )
  }, [dia, tipo, racao, quantidade, unidade, pesoSaco, fornecedor, marca,
      documento, cicloId, observacao])

  // Rascunho de um app anterior pode trazer `saida`, que deixou de existir. O
  // servidor recusaria, e a tela teria oferecido um botão que não funciona.
  const tipoValido: TipoDeMovimento = tipo === 'entrada' || tipo === 'perda' ? tipo : 'entrada'

  if (carregando) return <p className="aviso">Carregando…</p>
  if (galpao === null) {
    return <p className="aviso-erro">Não deu para abrir o galpão: {erro ?? 'sem resposta'}</p>
  }

  // O peso que ESTE lançamento vai usar para converter saco em quilo. Na
  // compra é o da compra; no resto é o da fazenda, que é o mesmo do trato.
  const pesoDaCompra = numeroNaFaixa(pesoSaco, 1, 100)
  const pesoUsado = tipoValido === 'entrada' ? (pesoDaCompra ?? galpao.peso_saco_kg) : galpao.peso_saco_kg

  const nQuantidade = numeroNaFaixa(quantidade, 0.5, 100_000)
  const quantidadeKg = nQuantidade === null ? null
    : unidade === 'saco' ? nQuantidade * pesoUsado : nQuantidade

  const sacoQuebrado =
    unidade === 'saco' && nQuantidade !== null && !Number.isInteger(nQuantidade * 2)

  const precisaMotivo = tipoValido === 'perda' && observacao.trim() === ''

  const pronto =
    racao !== null && nQuantidade !== null && !sacoQuebrado && !precisaMotivo

  function gravar() {
    if (!pronto || racao === null || nQuantidade === null) return
    setErro(null)
    try {
      registrarMovimentoDeRacao({
        data: dia,
        tipo: tipoValido,
        granulometria_min_mm: racao.min,
        granulometria_max_mm: racao.max,
        proteina_pct: racao.proteina,
        unidade_informada: unidade,
        quantidade_informada: nQuantidade,
        ...(tipoValido === 'entrada' && pesoDaCompra !== null
          ? { peso_saco_kg: pesoDaCompra } : {}),
        fabricante: marca.trim() === '' ? null : marca.trim(),
        fornecedor: fornecedor.trim() === '' ? null : fornecedor.trim(),
        documento: documento.trim() === '' ? null : documento.trim(),
        ciclo_id: tipoValido === 'perda' && cicloId !== '' ? cicloId : null,
        observacao: observacao.trim() === '' ? null : observacao.trim(),
      }, { rotulo: nomeDaRacao(racao) })

      // A RAÇÃO FICA, o resto sai. Quem lança três compras seguidas do mesmo
      // saco não quer reescolher a especificação três vezes; quem lança um
      // número errado duas vezes é porque o campo não se limpou.
      setQuantidade('')
      setDocumento('')
      setObservacao('')
      guardarRascunho(null)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <section className="bloco">
      <h2>Estoque de ração</h2>

      {podeVerCusto && (
        <ComprasSemPreco
          compras={galpao.sem_preco}
          aoLancar={(id) => setNota({ marcada: id, corrigindo: null })}
        />
      )}

      <div ref={ancoraDaNota} />
      {podeVerCusto && nota !== null && (
        <FormularioDaNota
          // A chave remonta o formulário quando se abre outra nota: estado de
          // uma nota vazando para a seguinte é valor digitado na compra errada.
          key={`${nota.corrigindo?.id ?? 'nova'}:${nota.marcada ?? ''}`}
          semPreco={galpao.sem_preco}
          marcada={nota.marcada}
          corrigindo={nota.corrigindo}
          aoFechar={() => setNota(null)}
          aoGravar={() => {
            setNota(null)
            aoGravar()
          }}
        />
      )}

      <NoGalpao galpao={galpao} podeOcultar={podeVerCusto} aoMudar={aoGravar} />

      <h3>Registrar</h3>

      {/* SÃO DOIS, E NENHUM É DE TODO DIA. O consumo entra sozinho, pelo
          trato — esta tela não tem lançamento de rotina. */}
      <div className="apuracao">
        {(['entrada', 'perda'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={tipoValido === t ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => setTipo(t)}
          >
            {t === 'entrada' ? 'chegou ração' : 'perdeu ração'}
          </button>
        ))}
      </div>

      <p className="secundario">
        {tipoValido === 'entrada'
          ? 'Compra que chegou ao galpão. O consumo desconta sozinho, pelo trato.'
          : 'Ração que não vai chegar ao peixe: molhou, rasgou, venceu.'}
      </p>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      <EscolherRacao parametros={parametros} escolhida={racao} aoEscolher={setRacao} />

      <label className="campo">
        <span className="campo-rotulo">Quanto</span>
        <input
          value={quantidade}
          onChange={(e) => setQuantidade(soNumero(e.target.value))}
          inputMode="decimal"
          placeholder={unidade === 'saco' ? '6' : '150'}
        />
      </label>

      <div className="apuracao compacta">
        {(['saco', 'kg'] as const).map((u) => (
          <button
            key={u}
            type="button"
            className={unidade === u ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => setUnidade(u)}
          >
            {u}
          </button>
        ))}
      </div>

      {sacoQuebrado && (
        <p className="aviso-erro">Saco só aceita inteiro ou meio. Para fração, informe em kg.</p>
      )}

      {/* O PESO DO SACO APARECE SEMPRE QUE ELE ESTIVER SENDO USADO. Conversão
          de unidade calada é a classe de erro que este projeto inteiro existe
          para evitar — e aqui ela decidiria quantos quilos entram no galpão. */}
      {unidade === 'saco' && quantidadeKg !== null && (
        <p className="secundario">
          {numero(quantidadeKg, 0)} kg, a {numero(pesoUsado, 0)} kg o saco.
        </p>
      )}

      {tipoValido === 'entrada' && (
        <>
          <label className="campo">
            <span className="campo-rotulo">Peso do saco desta compra</span>
            <input
              value={pesoSaco}
              onChange={(e) => setPesoSaco(soNumero(e.target.value))}
              inputMode="decimal"
              placeholder={numero(galpao.peso_saco_kg, 0)}
            />
            <span className="campo-dica">
              Vem com o peso de saco da fazenda. Troque quando esta compra vier em saco de
              outro peso — 25 e 30 kg convivem na mesma pilha.
            </span>
          </label>

          <label className="campo">
            <span className="campo-rotulo">Marca (opcional)</span>
            <input value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Laguna" />
          </label>

          {/* LISTA FECHADA, pela razão do gestor: «quando eu for comparar
              ração entre diferentes fornecedores, os cadastros precisam estar
              corretos». Campo livre aceita «Agro Ariquemes», «agro ariquemes»
              e «Agro  Ariquemes» — três fornecedores que são um só.

              «Outro» continua existindo porque o caminhão novo chega sem
              avisar, e o nome digitado ali entra na lista assim que sincroniza.
              Quem resolve o nome é o servidor, não este aparelho: dois
              celulares sem sinal cadastrando o mesmo fornecedor têm de
              convergir para a mesma linha sem combinar nada. */}
          <label className="campo">
            <span className="campo-rotulo">Fornecedor (opcional)</span>
            <select
              value={fornecedorNovo ? '__novo' : fornecedor}
              onChange={(e) => {
                if (e.target.value === '__novo') {
                  setFornecedorNovo(true)
                  setFornecedor('')
                } else {
                  setFornecedorNovo(false)
                  setFornecedor(e.target.value)
                }
              }}
            >
              <option value="">escolha…</option>
              {fornecedores.map((f) => (
                <option key={f.id} value={f.nome}>{f.nome}</option>
              ))}
              <option value="__novo">+ outro fornecedor</option>
            </select>
          </label>

          {fornecedorNovo && (
            <label className="campo">
              <span className="campo-rotulo">Nome do fornecedor novo</span>
              <input
                value={fornecedor}
                onChange={(e) => setFornecedor(e.target.value)}
                placeholder="Agro Ariquemes"
              />
              <span className="campo-dica">
                Entra na lista assim que sincronizar. Escrito de outro jeito, vira outro
                fornecedor — por isso vale conferir antes.
              </span>
            </label>
          )}

          <label className="campo">
            <span className="campo-rotulo">Nota fiscal (opcional)</span>
            <input
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              placeholder="12345"
            />
          </label>

          {/* O VALOR NÃO ENTRA AQUI desde 25/09/2026. A compra é o que chegou;
              o preço é da nota, lançado à parte pelo escritório — com desconto
              e frete, que não são conta para a beira do barracão. Quem lança
              a compra e tem a nota na mão lança o preço logo em seguida, na
              lista «Compras sem preço». */}
        </>
      )}

      {tipoValido === 'perda' && (
        <label className="campo">
          <span className="campo-rotulo">Em qual represa (opcional)</span>
          <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
            <option value="">no galpão / não se aplica</option>
            {ciclos.map((c) => (
              <option key={c.id} value={c.id}>{c.tanque} · {c.codigo}</option>
            ))}
          </select>
          <span className="campo-dica">
            Só quando a ração se perdeu já na beira de um tanque. A perda no galpão
            não tem represa.
          </span>
        </label>
      )}

      <label className="campo">
        <span className="campo-rotulo">
          {tipoValido === 'perda' ? 'O que aconteceu?' : 'Observação (opcional)'}
        </span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder={tipoValido === 'perda' ? 'saco molhado na chuva' : ''}
        />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <button type="button" className="confirma" disabled={!pronto} onClick={gravar}>
        ✓ registrar
      </button>

      <UltimosMovimentos
        movimentos={galpao.movimentos}
        aoMudar={aoGravar}
        escritorio={podeVerCusto}
        aoLancarPreco={(id) => setNota({ marcada: id, corrigindo: null })}
        aoCorrigirPreco={(notaId) => {
          const n = galpao.notas.find((x) => x.id === notaId) ?? null
          if (n !== null) setNota({ marcada: null, corrigindo: n })
        }}
      />
    </section>
  )
}

/**
 * O QUE TEM NO GALPÃO — e é a pergunta que traz alguém a esta tela.
 *
 * Uma ração por bloco, e dentro dele as PILHAS, porque a pilha é a coisa que
 * existe fisicamente. O peixeiro está em pé na frente dela: se a tela espelhar
 * o galpão, a conferência é olhar, e não é uma tarefa.
 *
 * TRÊS ZONAS, e cada uma existe por uma razão diferente:
 *
 *   ABERTA     é UMA só, e é a única que pede conferência. Leva a barra, o
 *              número para comparar com a pilha de verdade, e o botão.
 *   NA FILA    estoque parado. Só o total e a data — não há o que conferir.
 *   FECHADAS   o histórico das diferenças, que é a parte valiosa: acumulado,
 *              deixa de ser conferência e vira calibragem.
 */
function NoGalpao({
  galpao, podeOcultar, aoMudar,
}: {
  galpao: Galpao
  podeOcultar: boolean
  aoMudar: () => void
}) {
  const total = galpao.racoes.reduce((s, r) => s + r.saldo_kg, 0)
  const consumo = galpao.racoes.reduce((s, r) => s + r.consumido_kg, 0)
  const ritmo = galpao.racoes.reduce((s, r) => s + r.ritmo_kg_dia, 0)
  const dias = diasDeRacao(total, ritmo)
  const semPrevisao = porQueNaoDaParaEstimar(total, ritmo)

  // O QUE ESTÁ À VISTA E O QUE ESTÁ GUARDADO. Ração oculta com saldo continua
  // aparecendo: desativar nunca foi um jeito de sumir com quilo.
  const aVista = galpao.racoes.filter((r) => r.ativa || Math.abs(r.saldo_kg) >= 0.5)
  const ocultas = galpao.racoes.filter((r) => !r.ativa && Math.abs(r.saldo_kg) < 0.5)

  if (aVista.length === 0 && ocultas.length === 0) {
    return (
      <p className="aviso">
        Nenhuma ração no galpão ainda. Registre a primeira compra em «chegou ração».
      </p>
    )
  }

  return (
    <>
      <div className="galpao-topo">
        <strong>{inteiro(total)} kg no galpão</strong>
        <span className="secundario">
          {' '}· {inteiro(consumo)} kg nos últimos {galpao.dias_consumo} dias
        </span>
        <br />
        {/* ESTIMATIVA, e ela se apresenta como uma (regra 8). O ritmo de uma
            semana não prevê a seguinte: o peixe cresce, a chuva derruba a
            oferta. Serve para uma pergunta só — «compro esta semana?». */}
        {/* AS DUAS RAZÕES DE NÃO HAVER PREVISÃO TÊM FRASES DIFERENTES.
            A tela dizia «sem trato nos últimos 7 dias» também quando o galpão
            estava negativo — com 17.588 kg tratados no mês na linha logo
            acima, mandando procurar um problema de trato que não existia e
            escondendo o de verdade, que é a compra faltando. */}
        {dias !== null && (
          <span className="secundario">
            Dá para cerca de {dias} {dias === 1 ? 'dia' : 'dias'}, no ritmo dos últimos{' '}
            {galpao.dias_ritmo} ({inteiro(ritmo)} kg/dia).
          </span>
        )}
        {semPrevisao === 'saldo_negativo' && (
          <span className="galpao-recado">
            O consumo registrado já passou as compras lançadas. Enquanto faltar compra não há
            quanto dura — e o que falta é lançar nota, não ração.
          </span>
        )}
        {semPrevisao === 'sem_ritmo' && (
          <span className="secundario">
            Sem trato registrado nos últimos {galpao.dias_ritmo} dias — não dá para estimar a
            duração.
          </span>
        )}
      </div>

      {aVista.map((r) => (
        <RacaoNoGalpao
          key={r.racao_id}
          racao={r}
          hoje={galpao.hoje}
          podeOcultar={podeOcultar}
          aoMudar={aoMudar}
        />
      ))}

      {ocultas.length > 0 && (
        <Ocultas racoes={ocultas} podeMostrar={podeOcultar} aoMudar={aoMudar} />
      )}
    </>
  )
}

/**
 * A CHAVE DE VOLTA.
 *
 * A primeira versão de «ocultar» trancava a porta e jogava a chave fora — o
 * gestor pegou na hora: «por enquanto ele é uma função que tranca a porta do
 * quarto e joga a chave fora, não tem opção de reexibir».
 *
 * É o defeito que este projeto mais produz, e de novo do mesmo jeito: a
 * mecânica existia — a rota sempre aceitou `ativa: true` — e faltou o caminho
 * para alguém usá-la. Construir a defesa e não dar o sinal de que ela agiu,
 * construir a tranca e não dar a chave: é a mesma família.
 *
 * Fica no fim da tela, fechada, e só aparece quando há alguma coisa guardada.
 * Oculto tem de ser difícil de ver e fácil de achar — as duas coisas.
 */
function Ocultas({
  racoes, podeMostrar, aoMudar,
}: {
  racoes: RacaoDoGalpao[]
  podeMostrar: boolean
  aoMudar: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  return (
    <div className="ocultas">
      <button
        type="button"
        className="pilha-grupo-linha"
        onClick={() => setAberto(!aberto)}
        aria-expanded={aberto}
      >
        <span className="gaveta-seta">{aberto ? '▾' : '▸'}</span>
        {/* «ver as 1 ração oculta» foi o que saiu da primeira versão. Com uma
            só o número não acrescenta nada — e some. */}
        <span className="pilha-grupo-titulo">
          {racoes.length === 1
            ? 'ver a ração oculta'
            : `ver as ${racoes.length} rações ocultas`}
        </span>
      </button>

      {aberto && (
        <>
          {erro && <p className="aviso-erro">{erro}</p>}
          {racoes.map((r) => (
            <div key={r.racao_id} className="pilha-linha">
              <span className="secundario">{nomeDaRacao(espec(r))}</span>
              {podeMostrar && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => {
                    setErro(null)
                    ocultarRacao(r.racao_id, true)
                      .then(aoMudar)
                      .catch((falha: unknown) => {
                        setErro(falha instanceof Error ? falha.message : 'não deu para mostrar')
                      })
                  }}
                >
                  trazer de volta
                </button>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  )
}

/**
 * O olho, desenhado à mão.
 *
 * Dezoito pixels de SVG contra uma biblioteca de ícones inteira, pelo mesmo
 * motivo do gráfico: o app abre na beira do tanque com sinal ruim, e cada
 * quilobyte é do tratador esperando.
 */
function Olho({ fechado = false }: { fechado?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="olho-svg" aria-hidden="true">
      <path
        d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
      {fechado && (
        <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth="1.8" />
      )}
    </svg>
  )
}

function RacaoNoGalpao({
  racao, hoje, podeOcultar, aoMudar,
}: {
  racao: RacaoDoGalpao
  hoje: string
  podeOcultar: boolean
  aoMudar: () => void
}) {
  const [ocultando, setOcultando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const aberta = racao.pilhas.find((p) => p.estado === 'aberta') ?? null
  const naFila = racao.pilhas.filter((p) => p.estado === 'na_fila')
  // Da mais recente para a mais antiga: a última que fechou é a que interessa.
  const fechadas = racao.pilhas.filter((p) => p.estado === 'fechada').reverse()
  const semPilha = racao.consumo_sem_pilha_kg

  return (
    <div className="galpao">
      <div className="galpao-cabeca">
        <strong>{nomeDaRacao(espec(racao))}</strong>
        <span className="galpao-saldo">
          <Sacos kg={racao.saldo_kg} pesoSaco={racao.peso_saco_kg} />
        </span>

        {/* UM OLHO PEQUENO E APAGADO, a pedido do gestor. Ocultar é limpeza de
            cadastro, não operação: não pode disputar atenção com o saldo, que
            é a razão de alguém abrir esta tela.

            Só com o galpão zerado, e só para o gestor. Ração com quilo dentro
            não se esconde — a tela deixaria de bater com a pilha. O servidor
            recusa de qualquer jeito; aqui o botão nem aparece, que é melhor
            que um botão explicando por que não funciona. */}
        {podeOcultar && Math.abs(racao.saldo_kg) < 0.5 && (
          <button
            type="button"
            className="olho"
            onClick={() => setOcultando(!ocultando)}
            aria-label={`ocultar ${nomeDaRacao(espec(racao))}`}
            title="ocultar esta ração"
          >
            <Olho fechado />
          </button>
        )}
      </div>

      {ocultando && (
        <div className="galpao-ocultar">
          <span className="secundario">
            Ocultar esta ração? Ela sai da lista, e você a traz de volta pelo «ver as
            ocultas» no fim da tela.
          </span>
          <button
            type="button"
            className="confirma"
            onClick={() => {
              setErro(null)
              ocultarRacao(racao.racao_id, false)
                .then(() => { setOcultando(false); aoMudar() })
                .catch((falha: unknown) => {
                  setErro(falha instanceof Error ? falha.message : 'não deu para ocultar')
                })
            }}
          >
            sim, ocultar
          </button>
          <button type="button" className="discreto" onClick={() => setOcultando(false)}>
            deixa
          </button>
        </div>
      )}
      {erro && <p className="aviso-erro">{erro}</p>}

      {/* CONSUMO SEM PILHA — o peixe comeu ração que o galpão não tinha.
          Dois casos com a mesma cara, e a frase muda porque a ação muda:
          com compras lançadas falta uma nota; sem nenhuma, o mais provável é
          a especificação escolhida no trato não existir na pilha, e mandar
          lançar a compra faria alguém inventar uma para calar o aviso. */}
      {semPilha > 0 && (
        <p className="galpao-recado">
          {nuncaComprada(racao.entrou_kg) ? (
            <>
              o peixe comeu {inteiro(semPilha)} kg desta ração e nenhuma compra dela foi
              lançada — confira se é esta a ração da pilha
            </>
          ) : (
            <>faltam {inteiro(semPilha)} kg de compra: o consumo passou o que entrou</>
          )}
        </p>
      )}

      {aberta !== null && (
        <PilhaAberta
          pilha={aberta}
          acabaEm={racao.acaba_em}
          hoje={hoje}
          aoMudar={aoMudar}
        />
      )}

      {naFila.length > 0 && (
        <div className="pilha-grupo">
          <span className="pilha-grupo-titulo">na fila</span>
          {naFila.map((p) => (
            <div key={p.id} className="pilha-linha">
              <span>{diaCurto(p.data)}{p.fabricante ? ` · ${p.fabricante}` : ''}</span>
              <span className="galpao-saldo">
                <Sacos kg={p.restante_kg} pesoSaco={p.peso_saco_kg} />
              </span>
            </div>
          ))}
        </div>
      )}

      {fechadas.length > 0 && (
        <Fechadas pilhas={fechadas} calibragem={racao.calibragem} aoMudar={aoMudar} />
      )}
    </div>
  )
}

/**
 * A PILHA ABERTA — a única linha que pede alguma coisa de quem está lendo.
 *
 * A barra mostra O QUE RESTA, e não o que foi consumido: é a altura da pilha
 * que ele vê. Barra que enche conforme a pilha esvazia seria a leitura ao
 * contrário da coisa que está na frente dele.
 */
function PilhaAberta({
  pilha, acabaEm, hoje, aoMudar,
}: {
  pilha: Pilha
  acabaEm: string | null
  hoje: string
  aoMudar: () => void
}) {
  const [fechando, setFechando] = useState(false)
  const [sobra, setSobra] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const nSobra = numeroNaFaixa(sobra, 0, 100_000)

  function fechar(sobrouKg: number) {
    setErro(null)
    try {
      fecharPilha(
        { remessa_id: pilha.id, data: hoje, sobrou_kg: sobrouKg },
        { rotulo: `pilha de ${diaCurto(pilha.data)}` },
      )
      setFechando(false)
      setSobra('')
      aoMudar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <div className="pilha aberta">
      <div className="pilha-titulo">
        <span>
          {pilha.fabricante ? <strong>{pilha.fabricante} · </strong> : null}
          chegou {diaCurto(pilha.data)}
          <span className="secundario">
            {' '}· <Sacos kg={pilha.kg} pesoSaco={pilha.peso_saco_kg} apenasSacos />
          </span>
        </span>
      </div>

      {/* A BARRA É MEDIDOR, NÃO GRÁFICO. Ela responde «quanto falta» antes de
          alguém ler o número — e é exatamente o que se quer de quem está com
          a pilha na frente e o sol na tela. */}
      <div
        className="pilha-barra"
        role="img"
        aria-label={`restam ${Math.round(pilha.fracao_restante * 100)}% da pilha`}
      >
        <div
          className="pilha-barra-resta"
          style={{ width: `${Math.max(0, Math.min(100, pilha.fracao_restante * 100))}%` }}
        />
      </div>

      <div className="pilha-numero">
        <strong>
          restam <Sacos kg={pilha.restante_kg} pesoSaco={pilha.peso_saco_kg} />
        </strong>
        {acabaEm !== null && (
          <span className="secundario"> · acaba por volta de {diaCurto(acabaEm)}</span>
        )}
      </div>

      {!fechando ? (
        <button type="button" className="discreto" onClick={() => setFechando(true)}>
          conferir esta pilha
        </button>
      ) : (
        <div className="pilha-conferir">
          {/* O APP FALA E O HUMANO CORRIGE. A pergunta já vem com a resposta
              do app do lado, porque o gesto mais comum é concordar — e quem
              concorda não precisa fazer nada. */}
          <p>
            O app conta{' '}
            <strong>
              <Sacos kg={pilha.restante_kg} pesoSaco={pilha.peso_saco_kg} />
            </strong>{' '}
            nesta pilha. E na sua frente?
          </p>

          <button type="button" className="confirma" onClick={() => fechar(0)}>
            acabou
          </button>

          <label className="campo">
            <span className="campo-rotulo">ou ainda tem, em kg</span>
            <input
              value={sobra}
              onChange={(e) => setSobra(soNumero(e.target.value))}
              inputMode="decimal"
              placeholder={inteiro(pilha.restante_kg)}
            />
          </label>
          {nSobra !== null && (
            <p className="secundario">
              {Math.abs(nSobra - pilha.restante_kg) < 0.5
                ? 'Igual ao que o app conta — nada a corrigir.'
                : `Corrige o saldo em ${nSobra > pilha.restante_kg ? '+' : '−'}${inteiro(
                    Math.abs(nSobra - pilha.restante_kg),
                  )} kg.`}
            </p>
          )}
          {nSobra !== null && (
            <button type="button" className="confirma" onClick={() => fechar(nSobra)}>
              ✓ é isto
            </button>
          )}

          {erro && <p className="aviso-erro">{erro}</p>}
          <button type="button" className="discreto" onClick={() => setFechando(false)}>
            deixa
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * AS PILHAS FECHADAS — a parte valiosa, e a que ninguém digitou.
 *
 * ── POR QUE DUAS MEDIDAS E NÃO UMA ────────────────────────────────────────
 *
 * A diferença de UMA pilha não diz nada: fecha com sobra num mês e com falta
 * no outro, e isso é o mundo. O que diz alguma coisa é separar o VIÉS — a
 * média, que quando é sistemática se conserta — da DISPERSÃO, que é ruído e
 * não se conserta. Um número só faz qualquer pessoa tratar ruído como
 * problema, e depois ignorar o aviso no dia em que ele for real.
 *
 * E o nome importa: é «diferença no fechamento», nunca «desperdício». Ela
 * mistura três coisas — disciplina de pilha, precisão do trato e perda de
 * verdade. Mesma natureza do saldo residual do ciclo, que a regra 4 define
 * como diagnóstico e nunca acusação.
 */
function Fechadas({
  pilhas, calibragem, aoMudar,
}: {
  pilhas: Pilha[]
  calibragem: Calibragem
  aoMudar: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const conferidas = pilhas.filter((p) => p.diferenca_kg !== null)

  return (
    <div className="pilha-grupo">
      <button
        type="button"
        className="pilha-grupo-linha"
        onClick={() => setAberto(!aberto)}
        aria-expanded={aberto}
      >
        <span className="gaveta-seta">{aberto ? '▾' : '▸'}</span>
        <span className="pilha-grupo-titulo">
          {pilhas.length} {pilhas.length === 1 ? 'pilha fechada' : 'pilhas fechadas'}
        </span>
        {calibragem.confiavel && calibragem.vies !== null && (
          <span className="pilha-vies">
            {calibragem.vies >= 0 ? '+' : '−'}
            {numero(Math.abs(calibragem.vies) * 100, 1)}% em média
          </span>
        )}
      </button>

      {aberto && (
        <>
          {conferidas.length > 0 && !calibragem.confiavel && (
            <p className="secundario">
              {conferidas.length} {conferidas.length === 1 ? 'pilha conferida' : 'pilhas conferidas'}{' '}
              — a média só começa a significar alguma coisa a partir de {PILHAS_PARA_CONFIAR}.
            </p>
          )}
          {calibragem.confiavel && calibragem.vies !== null && calibragem.dispersao !== null && (
            <p className="secundario">
              Em {calibragem.pilhas} pilhas, as diferenças variam{' '}
              {numero(calibragem.dispersao * 100, 1)} pontos em torno da média. Média muito
              maior que essa variação é sinal de coisa sistemática — peso do saco, ou trato
              relatado por baixo. Menor, é ruído.
            </p>
          )}

          {pilhas.map((p) => (
            <LinhaFechada key={p.id} pilha={p} aoMudar={aoMudar} />
          ))}
        </>
      )}
    </div>
  )
}

function LinhaFechada({ pilha, aoMudar }: { pilha: Pilha; aoMudar: () => void }) {
  const [desfazendo, setDesfazendo] = useState(false)
  const dif = pilha.diferenca_kg
  const dentroDoPrazo =
    pilha.fechada_em !== null && diasAtras(pilha.fechada_em) <= 3 && dif !== null

  return (
    <>
      <div className="pilha-linha">
        <span>
          {diaCurto(pilha.data)}
          <span className="secundario">
            {' '}· <Sacos kg={pilha.kg} pesoSaco={pilha.peso_saco_kg} apenasSacos />
            {pilha.fechada_em !== null && ` · fechou ${diaCurto(pilha.fechada_em)}`}
          </span>
        </span>
        {dif === null ? (
          <span className="secundario">acabou na conta</span>
        ) : (
          <span className={dif < 0 ? 'pilha-dif falta' : 'pilha-dif sobra'}>
            {dif < 0 ? '−' : '+'}
            {inteiro(Math.abs(dif))} kg
          </span>
        )}
      </div>
      {dentroDoPrazo && (
        <div className="pilha-linha">
          {desfazendo ? (
            <>
              <span className="secundario">
                Desfaz a conferência e a correção de saldo que veio com ela.
              </span>
              <button
                type="button"
                className="confirma"
                onClick={() => {
                  anularFechamento(pilha.id, pilha.fechada_em ?? pilha.data)
                  setDesfazendo(false)
                  aoMudar()
                }}
              >
                sim, desfazer
              </button>
            </>
          ) : (
            <button type="button" className="discreto" onClick={() => setDesfazendo(true)}>
              fechei a pilha errada
            </button>
          )}
        </div>
      )}
    </>
  )
}

/**
 * Saco E quilo lado a lado.
 *
 * A pilha se conta em sacos e a conta se faz em quilos. Mostrar um só
 * obrigaria a converter de cabeça justamente quem está em pé na frente dela.
 */
function Sacos({
  kg, pesoSaco, apenasSacos = false,
}: {
  kg: number
  pesoSaco: number
  apenasSacos?: boolean
}) {
  const sacos = emSacosDe(kg, pesoSaco)
  return (
    <>
      {/* Casa decimal só quando existe: «48,0 sc» é ruído numa pilha inteira,
          e «6,5 sc» precisa da casa. */}
      {sacos !== null && `${numero(sacos, Number.isInteger(sacos) ? 0 : 1)} sc`}
      {!apenasSacos && (
        <span className="secundario">
          {sacos !== null ? ' · ' : ''}
          {inteiro(kg)} kg
        </span>
      )}
    </>
  )
}

/** Quantos dias fazem desde aquele dia. Para o prazo de desfazer. */
function diasAtras(iso: string): number {
  const d = new Date(`${iso}T12:00:00Z`).getTime()
  if (Number.isNaN(d)) return 999
  return Math.floor((Date.now() - d) / 86_400_000)
}

/**
 * DESFAZER MORA ONDE O LANÇAMENTO APARECE.
 *
 * Construído em 22/09/2026, depois de uma nota fiscal entrar na ração errada e
 * virar caso de painel da Neon — porque a tela tinha caminho de ida e não
 * tinha caminho de volta, num lugar onde errar é normal.
 *
 * O prazo é de SETE dias, e não os três do trato. Trato é registro diário e
 * número antigo já foi lido por alguém; nota fiscal chega em lote, se confere
 * contra o extrato, e o erro aparece quando alguém soma a coluna.
 *
 * Quem recusa de verdade é o servidor, e ele tem uma trava que a tela não
 * consegue ter: se alguma pilha daquela ração já foi conferida depois do
 * lançamento, desfazer faria a diferença registrada no fechamento medir uma
 * coisa que nunca aconteceu.
 */
const DIAS_PARA_DESFAZER = 7

function UltimosMovimentos({
  movimentos, aoMudar, escritorio, aoLancarPreco, aoCorrigirPreco,
}: {
  movimentos: MovimentoDeRacao[]
  aoMudar: () => void
  /** Gestor ou ADM: vê o preço e pode lançá-lo. */
  escritorio: boolean
  aoLancarPreco: (compraId: string) => void
  aoCorrigirPreco: (notaId: string) => void
}) {
  const [desfazendo, setDesfazendo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const fila = precosNaFila()
  const naFila = fila.compras

  if (movimentos.length === 0) return null
  return (
    <>
      <h3>Últimos movimentos</h3>
      {erro && <p className="aviso-erro">{erro}</p>}
      <ul className="fila-lista">
        {movimentos.map((m) => (
          <li key={m.id} className="fila-item">
            <div className="fila-texto">
              <strong>
                {/* `saiu` e `ajuste` não se escrevem mais, e continuam
                    legíveis: o livro-razão guarda o que já foi gravado. */}
                {m.tipo === 'entrada' ? 'chegou' : m.tipo === 'saida' ? 'saiu'
                  : m.tipo === 'perda' ? 'perdeu' : 'ajuste'}{' '}
                {inteiro(m.quantidade_kg)} kg
              </strong>
              {' · '}
              {nomeDaRacao(
                m.granulometria_min_mm !== null && m.granulometria_max_mm !== null &&
                m.proteina_pct !== null
                  ? { min: m.granulometria_min_mm, max: m.granulometria_max_mm, proteina: m.proteina_pct }
                  : null,
              )}
              <br />
              <span className="secundario">
                {diaCurto(m.data)}
                {m.fornecedor ? ` · ${m.fornecedor}` : ''}
                {m.documento ? ` · nota ${m.documento}` : ''}
                {m.ciclo ? ` · ${m.ciclo}` : ''}
                {m.observacao ? ` · ${m.observacao}` : ''}
              </span>

              {/* O TRATADOR não vê o preço, mas vê por que não há desfazer: o
                  botão sumido sem explicação parece defeito. */}
              {!escritorio && m.tem_preco && (
                <div className="secundario">
                  com preço lançado — para desfazer, fale com o escritório
                </div>
              )}

              {/* O PREÇO, SÓ NO ESCRITÓRIO. Com nota: quanto custou, por quilo
                  e por saco, e quem lançou. Sem nota: dito em ocre, com o
                  caminho ao lado — nunca um zero no lugar. */}
              {escritorio && m.tipo === 'entrada' && (
                <div className="secundario">
                  {m.preco && fila.notasSubstituidas.has(m.preco.nota_id) ? (
                    // A CORREÇÃO JÁ FOI FEITA, e está na fila. Mostrar o preço
                    // antigo com o botão de corrigir ao lado faria a pessoa
                    // achar que não pegou, e corrigir de novo.
                    <>correção do preço esperando sincronizar</>
                  ) : m.preco ? (
                    <>
                      {emReais(m.preco.preco_kg_brl)}/kg
                      {m.preco.preco_saco_brl !== null && <> · {emReais(m.preco.preco_saco_brl)}/sc</>}
                      {' · '}{emReais(m.preco.custo_brl)} no total
                      {m.preco.lancada_por ? ` · lançado por ${m.preco.lancada_por}` : ''}
                      {' '}
                      <button
                        type="button"
                        className="discreto"
                        onClick={() => m.preco && aoCorrigirPreco(m.preco.nota_id)}
                      >
                        corrigir o preço
                      </button>
                    </>
                  ) : naFila.has(m.id) ? (
                    <>preço lançado, esperando sincronizar</>
                  ) : (
                    <>
                      <span className="sem-preco">sem preço</span>{' '}
                      <button type="button" className="discreto" onClick={() => aoLancarPreco(m.id)}>
                        lançar preço da nota
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* A PERGUNTA FICA COLADA NA LINHA, e nomeia o lançamento pelo
                  que ele tem de reconhecível: o quilo e o dia. Solta no fim da
                  lista, «desfazer este?» não diz qual, e confirmar o errado
                  não tem desfazer barato. */}
              {desfazendo === m.id && (
                <div className="confirma-desfazer">
                  <span>
                    Desfazer <strong>{inteiro(m.quantidade_kg)} kg</strong> de{' '}
                    {diaCurto(m.data)}? Fica no histórico, marcado como desfeito, e sai
                    das contas do galpão.
                  </span>
                  <button
                    type="button"
                    className="confirma"
                    onClick={() => {
                      setErro(null)
                      try {
                        anularMovimentoDeRacao(m.id, m.data)
                        setDesfazendo(null)
                        aoMudar()
                      } catch (falha) {
                        setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
                      }
                    }}
                  >
                    sim, desfazer
                  </button>
                </div>
              )}
            </div>

            {/* O ajuste do fechamento de pilha não se desfaz aqui: ele sobe e
                desce junto com a conferência, e o botão dele é «fechei a pilha
                errada», na própria pilha. */}
            {/* Compra com preço não se desfaz por aqui: a nota divide desconto
                e frete entre as compras dela, e tirar uma redistribuiria o
                dinheiro sobre as outras. Desfaz-se a nota primeiro. */}
            {/* Nem com preço na fila: pela ordem da fila, o desfazer subiria
                depois da nota e voltaria recusado. */}
            {m.tipo !== 'ajuste' && !m.preco && !m.tem_preco && !naFila.has(m.id) &&
              diasAtras(m.data) <= DIAS_PARA_DESFAZER && (
              <button
                type="button"
                className="discreto"
                onClick={() => setDesfazendo(desfazendo === m.id ? null : m.id)}
              >
                {desfazendo === m.id ? 'deixa' : 'desfazer'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * Número virando texto de CAMPO, que é coisa diferente de número virando
 * texto de leitura: aqui ele volta a ser lido por `numeroPtBr`, então não
 * pode levar separador de milhar. `numero()` poria, e «1.500» seria recusado
 * pelo próprio padrão numérico do sistema.
 */
function emTexto(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',')
}

/** «18/09» — o suficiente para saber de quando é. */
function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}

// ===========================================================================
// O preço da nota — o escritório
// ===========================================================================


function especDa(c: {
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
}): Especificacao | null {
  return c.granulometria_min_mm !== null && c.granulometria_max_mm !== null && c.proteina_pct !== null
    ? { min: c.granulometria_min_mm, max: c.granulometria_max_mm, proteina: c.proteina_pct }
    : null
}

/** «440 sc · 11.000 kg», ou só o quilo quando a compra veio em quilo. */
function quantoChegou(c: { quantidade_kg: number; peso_saco_kg: number | null }): string {
  const sacos = c.peso_saco_kg !== null && c.peso_saco_kg > 0 ? c.quantidade_kg / c.peso_saco_kg : null
  return sacos !== null
    ? `${numero(sacos, Number.isInteger(sacos) ? 0 : 1)} sc · ${inteiro(c.quantidade_kg)} kg`
    : `${inteiro(c.quantidade_kg)} kg`
}

/**
 * A LISTA DE TRABALHO DO ESCRITÓRIO — todas as compras sem preço.
 *
 * Todas, e não só as trinta últimas da lista de movimentos: a de dois meses
 * atrás é justamente a esquecida, e o aviso do topo do app conta TODAS. Aviso
 * que diz «37» e lista que mostra 30 é aviso que ninguém consegue zerar.
 */
function ComprasSemPreco({
  compras, aoLancar,
}: {
  compras: CompraSemPreco[]
  aoLancar: (compraId: string) => void
}) {
  if (compras.length === 0) return null
  const naFila = precosNaFila().compras
  return (
    <div className="sem-preco-lista">
      <h3>
        {compras.length} {compras.length === 1 ? 'compra sem preço' : 'compras sem preço'}
      </h3>
      <p className="secundario">
        Lançadas no barracão, esperando o preço da nota. Cuidado com descontos e frete: a
        tela divide os dois entre as compras da nota e mostra o total para você conferir com a
        nota fiscal.
      </p>
      <ul className="fila-lista">
        {compras.map((c) => (
          <li key={c.id} className="fila-item">
            <div className="fila-texto">
              <strong>{diaCurto(c.data)}</strong> · {nomeDaRacao(especDa(c))}
              <br />
              <span className="secundario">
                {quantoChegou(c)}
                {c.fornecedor ? ` · ${c.fornecedor}` : ''}
                {c.documento ? ` · nota ${c.documento}` : ''}
              </span>
            </div>
            {naFila.has(c.id) ? (
              <span className="secundario">na fila</span>
            ) : (
              <button type="button" className="discreto" onClick={() => aoLancar(c.id)}>
                lançar preço da nota
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Uma compra que pode entrar na nota — sem preço, ou já nesta nota. */
interface Candidata {
  id: string
  data: string
  quantidade_kg: number
  peso_saco_kg: number | null
  documento: string | null
  fornecedor: string | null
  espec: Especificacao | null
}

const POR_QUE_NAO_FECHA: Record<SemRateio, string> = {
  sem_itens: 'Marque ao menos uma compra.',
  valor_invalido: 'Algum valor não é um número. Use vírgula para os centavos: 1.234,56.',
  quantidade_zero: 'Uma das compras está com quantidade zero.',
  saco_sem_peso:
    'Uma das compras foi lançada em quilo, e o app não sabe quantos sacos eram. Lance esta nota em quilos.',
  desconto_maior: 'O desconto é maior que o valor dos itens.',
  custo_negativo: 'Com este desconto, uma compra ficaria com custo negativo. Confira os valores.',
}

/**
 * O FORMULÁRIO DA NOTA — lançar, corrigir ou desfazer.
 *
 * A quantidade de cada compra NÃO se digita: vem da própria compra. O que se
 * digita é o que está no papel — o valor de cada item, o desconto, o frete — e
 * a tela mostra, enquanto se digita, o que a divisão fez com cada compra e o
 * total, para conferir com a nota fiscal antes de gravar.
 *
 * «Burocracia desanima», disse o gestor: corrigir é abrir preenchido e salvar,
 * desfazer é um botão. Por baixo, o livro-razão guarda a nota antiga (regra 5).
 * A confirmação diz o total, porque é o total que se confere contra o papel.
 */
function FormularioDaNota({
  semPreco, marcada, corrigindo, aoFechar, aoGravar,
}: {
  semPreco: CompraSemPreco[]
  marcada: string | null
  corrigindo: NotaDeRacao | null
  aoFechar: () => void
  aoGravar: () => void
}) {
  const candidatas: Candidata[] = useMemo(() => {
    const daNota: Candidata[] = (corrigindo?.itens ?? []).map((i) => ({
      id: i.mov_racao_id, data: i.data, quantidade_kg: i.quantidade_kg,
      peso_saco_kg: i.peso_saco_kg, documento: i.documento, fornecedor: i.fornecedor,
      espec: especDa(i),
    }))
    // Compra com preço já na fila não se oferece de novo: a segunda nota
    // subiria atrás da primeira e voltaria recusada por «já tem preço».
    const naFila = precosNaFila().compras
    const livres: Candidata[] = semPreco
      .filter((c) => !daNota.some((d) => d.id === c.id) && !naFila.has(c.id))
      .map((c) => ({
        id: c.id, data: c.data, quantidade_kg: c.quantidade_kg, peso_saco_kg: c.peso_saco_kg,
        documento: c.documento, fornecedor: c.fornecedor, espec: especDa(c),
      }))
    return [...daNota, ...livres].sort((a, b) => a.data.localeCompare(b.data))
  }, [semPreco, corrigindo])

  // Valor digitado de cada compra MARCADA. Desmarcar tira da nota.
  const [valores, setValores] = useState<Map<string, string>>(() => {
    const m = new Map<string, string>()
    if (corrigindo) for (const i of corrigindo.itens) m.set(i.mov_racao_id, emCampo(i.valor_brl))
    else if (marcada) m.set(marcada, '')
    return m
  })
  const primeira = candidatas.find((c) => valores.has(c.id)) ?? null
  const [documento, setDocumento] = useState(corrigindo?.documento ?? primeira?.documento ?? '')
  const [desconto, setDesconto] = useState(corrigindo ? emCampo(corrigindo.desconto_brl) : '')
  const [frete, setFrete] = useState(corrigindo ? emCampo(corrigindo.frete_brl) : '')
  const [observacao, setObservacao] = useState(corrigindo?.observacao ?? '')

  const marcadas = candidatas.filter((c) => valores.has(c.id))
  // Nota em sacos só quando todas as compras marcadas vieram em saco.
  const sacoPossivel = marcadas.length > 0 && marcadas.every((c) => c.peso_saco_kg !== null)
  const [unidadeEscolhida, setUnidade] = useState<UnidadeDaNota>(
    corrigindo?.unidade ?? (primeira?.peso_saco_kg != null ? 'saco' : 'kg'),
  )
  const unidade: UnidadeDaNota = unidadeEscolhida === 'saco' && !sacoPossivel ? 'kg' : unidadeEscolhida

  const [confirmando, setConfirmando] = useState<'lancar' | 'desfazer' | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  // Campo vazio no desconto e no frete é zero; campo preenchido que não vira
  // número é erro, e não zero — zero calado seria a nota sair sem o frete.
  const lido = (t: string): number | null => (t.trim() === '' ? 0 : numeroPtBr(t))
  const descontoBrl = lido(desconto)
  const freteBrl = lido(frete)
  const itens = marcadas.map((c) => ({
    id: c.id,
    valor_brl: numeroPtBr(valores.get(c.id) ?? '') ?? NaN,
    quantidade_kg: c.quantidade_kg,
    peso_saco_kg: c.peso_saco_kg,
  }))
  const paraRatear = {
    unidade,
    desconto_brl: descontoBrl ?? NaN,
    frete_brl: freteBrl ?? NaN,
    itens,
  }
  const motivo = porQueNaoRateia(paraRatear)
  const rateio = ratearNota(paraRatear)
  const faltaValor = marcadas.some((c) => (valores.get(c.id) ?? '').trim() === '')

  function alternar(id: string) {
    setValores((atual) => {
      const nova = new Map(atual)
      if (nova.has(id)) nova.delete(id)
      else nova.set(id, '')
      return nova
    })
    setConfirmando(null)
  }

  function gravar(desfazer: boolean) {
    setErro(null)
    try {
      if (desfazer) {
        if (corrigindo === null) return
        lancarNotaDeRacao(
          {
            documento: corrigindo.documento, unidade: corrigindo.unidade,
            desconto_brl: 0, frete_brl: 0, observacao: null, itens: [],
            anula_nota_id: corrigindo.id,
          },
          'desfazer a nota',
        )
      } else {
        if (rateio === null || descontoBrl === null || freteBrl === null) return
        lancarNotaDeRacao(
          {
            documento: documento.trim() === '' ? null : documento.trim(),
            unidade,
            desconto_brl: descontoBrl,
            frete_brl: freteBrl,
            observacao: observacao.trim() === '' ? null : observacao.trim(),
            itens: rateio.itens.map((i) => ({ mov_racao_id: i.id, valor_brl: i.valor_brl })),
            anula_nota_id: corrigindo?.id ?? null,
          },
          `${emReais(rateio.total_brl)} em ${rateio.itens.length} ${rateio.itens.length === 1 ? 'compra' : 'compras'}`,
        )
      }
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <div className="nota-form">
      <h3>{corrigindo ? 'Corrigir o preço da nota' : 'Lançar preço da nota'}</h3>
      <p className="secundario">
        Marque as compras que esta nota cobre e digite o valor de cada uma como está na nota.
        Desconto e frete se dividem pela quantidade, na unidade da nota.
      </p>

      <label className="campo">
        <span className="campo-rotulo">Número da nota (opcional)</span>
        <input value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder="12345" />
      </label>

      <div className="campo">
        <span className="campo-rotulo">A nota vem em</span>
        <div className="apuracao">
          {(['saco', 'kg'] as const).map((u) => (
            <button
              key={u}
              type="button"
              className={unidade === u ? 'unidade-botao ativo' : 'unidade-botao'}
              disabled={u === 'saco' && !sacoPossivel}
              onClick={() => setUnidade(u)}
            >
              {u === 'saco' ? 'sacos' : 'quilos'}
            </button>
          ))}
        </div>
        {!sacoPossivel && marcadas.length > 0 && (
          <span className="campo-dica">
            Em quilos, porque uma das compras foi lançada em quilo e o app não sabe quantos sacos
            eram.
          </span>
        )}
      </div>

      {candidatas.map((c) => {
        const marcado = valores.has(c.id)
        const r = rateio?.itens.find((i) => i.id === c.id) ?? null
        return (
          <div key={c.id} className="nota-compra">
            <label>
              <input type="checkbox" checked={marcado} onChange={() => alternar(c.id)} />
              <span>
                <strong>{diaCurto(c.data)}</strong> · {nomeDaRacao(c.espec)}
                <br />
                <span className="secundario">
                  {quantoChegou(c)}
                  {c.fornecedor ? ` · ${c.fornecedor}` : ''}
                  {c.documento ? ` · nota ${c.documento}` : ''}
                </span>
              </span>
            </label>
            {marcado && (
              <label className="campo">
                <span className="campo-rotulo">Valor desta ração na nota (R$)</span>
                <input
                  value={valores.get(c.id) ?? ''}
                  onChange={(e) => {
                    const v = soNumero(e.target.value)
                    setValores((atual) => new Map(atual).set(c.id, v))
                    setConfirmando(null)
                  }}
                  inputMode="decimal"
                  placeholder="44.000,00"
                />
              </label>
            )}
            {r && (
              <div className="nota-rateio secundario">
                {emReais(r.valor_brl)} − desconto {emReais(r.desconto_brl)} + frete{' '}
                {emReais(r.frete_brl)} = <strong>{emReais(r.custo_brl)}</strong>
                <br />
                {emReais(r.preco_kg_brl)}/kg
                {r.preco_saco_brl !== null && <> · {emReais(r.preco_saco_brl)}/sc</>}
              </div>
            )}
          </div>
        )
      })}

      <label className="campo">
        <span className="campo-rotulo">Descontos da nota, somados (R$)</span>
        <input
          value={desconto}
          onChange={(e) => { setDesconto(soNumero(e.target.value)); setConfirmando(null) }}
          inputMode="decimal"
          placeholder="0,00"
        />
      </label>
      <label className="campo">
        <span className="campo-rotulo">Frete (R$)</span>
        <input
          value={frete}
          onChange={(e) => { setFrete(soNumero(e.target.value)); setConfirmando(null) }}
          inputMode="decimal"
          placeholder="0,00"
        />
      </label>
      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </label>

      {rateio ? (
        <p className="nota-total">
          Total da nota: <strong>{emReais(rateio.total_brl)}</strong>
          <br />
          <span className="secundario">
            {emReais(rateio.total_itens_brl)} em itens − {emReais(descontoBrl ?? 0)} de desconto
            + {emReais(freteBrl ?? 0)} de frete. Confira com o total da nota fiscal.
          </span>
        </p>
      ) : (
        marcadas.length > 0 && !faltaValor && motivo !== null && (
          <p className="aviso-erro">{POR_QUE_NAO_FECHA[motivo]}</p>
        )
      )}

      {erro && <p className="aviso-erro">{erro}</p>}

      {/* DUPLA VERIFICAÇÃO, com o número que se confere contra o papel. */}
      {confirmando === 'lancar' && rateio ? (
        <div className="confirma-desfazer">
          <span>
            {corrigindo ? 'Trocar o preço desta nota por' : 'Lançar'}{' '}
            <strong>{emReais(rateio.total_brl)}</strong> para {rateio.itens.length}{' '}
            {rateio.itens.length === 1 ? 'compra' : 'compras'}? É o total da nota fiscal?
          </span>
          <button type="button" className="confirma" onClick={() => gravar(false)}>
            sim, lançar
          </button>
          <button type="button" className="discreto" onClick={() => setConfirmando(null)}>
            voltar
          </button>
        </div>
      ) : confirmando === 'desfazer' && corrigindo ? (
        <div className="confirma-desfazer">
          <span>
            Desfazer esta nota?{' '}
            {corrigindo.itens.length === 1
              ? 'A compra dela volta'
              : `As ${corrigindo.itens.length} compras dela voltam`}{' '}
            para «sem preço», e a nota fica no histórico, marcada como desfeita.
          </span>
          <button type="button" className="confirma" onClick={() => gravar(true)}>
            sim, desfazer
          </button>
          <button type="button" className="discreto" onClick={() => setConfirmando(null)}>
            voltar
          </button>
        </div>
      ) : (
        <div className="acoes">
          <button
            type="button"
            className="confirma"
            disabled={rateio === null || faltaValor}
            onClick={() => setConfirmando('lancar')}
          >
            {corrigindo ? 'salvar a correção' : 'lançar preço da nota'}
          </button>
          {corrigindo && (
            <button type="button" className="discreto" onClick={() => setConfirmando('desfazer')}>
              desfazer a nota
            </button>
          )}
          <button type="button" className="discreto" onClick={aoFechar}>
            cancelar
          </button>
        </div>
      )}
    </div>
  )
}

/** Valor em reais escrito como o campo o lê de volta: «44000,5», sem milhar. */
function emCampo(v: number): string {
  return v.toLocaleString('pt-BR', { useGrouping: false, maximumFractionDigits: 2 })
}
